import { logger } from '@adornis/base/logging.js';
import * as crypto from 'node:crypto';
import { GRADING_THRESHOLDS } from '../../../../shared/constants.js';
import { BoundingBox } from '../../../BoundingBox.js';
import {
  addErrorRegionByExam,
  addLowConfRegionByExam,
  ErrorRegion,
  LowConfRegion,
  type StepType,
} from '../../../extraction-progress.js';
import { Point } from '../../../Point.js';
import { type Submission, SubmissionTask, TaskGradingState } from '../../../submission.js';
import { aggregateOCRFromBoundingBoxes } from '../../../utils/exam-utils.js';
import type { ExtractionRegion } from './extraction-regions.js';
import { createBoundingBoxWithOCR } from './ocr-utils.js';
import { OcrExtractionType } from './ocrExtractionTypes.js';
import { TaskType } from '../../task-types/task-main.js';
const MEDIUM_CONFIDENCE_THRESHOLD = GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;

interface ProcessedRegionResult {
  region: ExtractionRegion;
  imageId: string | null | undefined;
  boundingBox: any;
}

function sortByPageAndY<T extends { region: ExtractionRegion }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.region.pageNumber !== b.region.pageNumber) {
      return a.region.pageNumber - b.region.pageNumber;
    }
    return a.region.bbox.y1 - b.region.bbox.y1;
  });
}

function regionPath(region: ExtractionRegion): string[] {
  const parts = (Array.isArray(region.taskPath) ? region.taskPath : [])
    .map(part => String(part ?? '').trim())
    .filter(Boolean);
  return parts;
}

function buildFallbackExamTaskTree(regions: ExtractionRegion[]): any[] {
  const roots = new Map<string, any>();

  const ensureNode = (siblings: any[], taskNumber: string): any => {
    let found = siblings.find(item => String(item?.taskNumber ?? '') === taskNumber);
    if (!found) {
      found = { taskNumber, subTasks: [] };
      siblings.push(found);
    }
    if (!Array.isArray(found.subTasks)) found.subTasks = [];
    return found;
  };

  for (const region of regions) {
    const path = regionPath(region);
    if (path.length === 0) continue;

    const [rootTaskMaybe, ...children] = path;
    const rootTask = String(rootTaskMaybe ?? '').trim();
    if (!rootTask) continue;
    if (!roots.has(rootTask)) {
      roots.set(rootTask, { taskNumber: rootTask, subTasks: [] });
    }

    let current = roots.get(rootTask);
    let siblings = current.subTasks as any[];
    for (const part of children) {
      current = ensureNode(siblings, part);
      siblings = current.subTasks;
    }
  }

  return Array.from(roots.values());
}

export async function createSubmissionTasks(
  regions: ExtractionRegion[],
  submission: Submission,
  examTasks: any[] = [],
  onCost?: (cost: number) => void,
  examID?: string,
  step?: StepType,
): Promise<SubmissionTask[]> {
  const tasksToProcess = examTasks.length > 0 ? examTasks : buildFallbackExamTaskTree(regions);

  // Process all regions in parallel: crop + OCR for each
  const processedResults = await Promise.all(
    regions.map(async region => {
      const submissionId = String(submission._id ?? '');
      const boundingBoxId = crypto.randomUUID();
      const path = regionPath(region);
      const imageIndex = region.pageNumber - 1;
      const fileId = submission.imageIds?.[imageIndex];
      if (!fileId) {
        if (examID && step) {
          await addErrorRegionByExam(
            examID,
            step,
            new ErrorRegion({
              submissionId,
              taskPath: path,
              additionalInfo: false,
              boundingBoxId,
            }),
          );
        }
      }
      try {
        const boundingBox = await createBoundingBoxWithOCR(
          {
            ...region,
            boundingBoxId,
          },
          submission.imageIds ?? [],
          OcrExtractionType.STUDENT_ANSWER,
          region.taskType || TaskType.DEFAULT,
          onCost,
        );

        if (
          examID &&
          step &&
          typeof boundingBox?.confidence === 'number' &&
          boundingBox.confidence < MEDIUM_CONFIDENCE_THRESHOLD
        ) {
          await addLowConfRegionByExam(
            examID,
            step,
            new LowConfRegion({
              submissionId,
              taskPath: path,
              additionalInfo: false,
              boundingBoxId: String(boundingBox._id ?? boundingBoxId),
            }),
          );
        }

        return {
          region,
          imageId: boundingBox.imageId,
          boundingBox,
        } satisfies ProcessedRegionResult;
      } catch (e) {
        logger.error({ err: e, submissionId: submission._id }, 'Processing failed for submission');

        const fallbackBbox = new BoundingBox({
          _id: boundingBoxId,
          topLeft: new Point({
            x: region.bbox.x1,
            y: region.bbox.y1,
          }),
          bottomRight: new Point({
            x: region.bbox.x2,
            y: region.bbox.y2,
          }),
          pageNumber: region.pageNumber,
          imageId: null,
          ocrExtractedText: '',
          confidence: 0,
          isHandwritten: false,
        });

        if (examID && step) {
          await addErrorRegionByExam(
            examID,
            step,
            new ErrorRegion({
              submissionId,
              taskPath: path,
              additionalInfo: false,
              boundingBoxId,
            }),
          );
        }

        return {
          region,
          imageId: null,
          boundingBox: fallbackBbox,
        } satisfies ProcessedRegionResult;
      }
    }),
  );

  const validResults = processedResults.filter((result): result is ProcessedRegionResult => result !== null);

  const resultsByPath = new Map<string, ProcessedRegionResult[]>();
  for (const result of validResults) {
    const path = regionPath(result.region);
    if (path.length === 0) continue;
    const key = path.join('|');
    if (!resultsByPath.has(key)) resultsByPath.set(key, []);
    resultsByPath.get(key)!.push(result);
  }

  const buildNode = async (task: any, parentPath: string[]): Promise<SubmissionTask> => {
    const taskNumber = String(task?.taskNumber ?? '').trim();
    const currentPath = [...parentPath, taskNumber];
    const key = currentPath.join('|');

    const orderedResults = sortByPageAndY(resultsByPath.get(key) ?? []);
    const nodeImageIds = orderedResults
      .map(r => r.imageId)
      .filter((imageId): imageId is string => typeof imageId === 'string' && imageId.length > 0);
    const nodeBoundingBoxes = orderedResults.map(r => r.boundingBox);

    const children = Array.isArray(task?.subTasks) ? task.subTasks : [];
    const subTasksSubmission = await Promise.all(children.map((child: any) => buildNode(child, currentPath)));

    return new SubmissionTask({
      _id: crypto.randomUUID(),
      taskNumber,
      imageIds: nodeImageIds,
      boundingBoxes: nodeBoundingBoxes,
      studentAnswer: aggregateOCRFromBoundingBoxes(nodeBoundingBoxes),
      subTasksSubmission,
      gradingState: TaskGradingState.PENDING,
    });
  };

  return Promise.all((Array.isArray(tasksToProcess) ? tasksToProcess : []).map(task => buildNode(task, [])));
}
